import React from 'react';
import geo from '../../data/dgs/cartes_geo.json';
import CompanyLogo from '../game/CompanyLogo';
import { evenementLocalActif } from '../../utils/evenementsVilles';
import { VILLES, NB_TYPES_MAGASIN } from '../../utils/villes';
import { concurrentDansVille, getEnseigne, logoEnseigne } from '../../utils/parc';

/* Couleurs par type de magasin + écusson de positionnement (partagés). */
export const FLAG_TYPE_COLORS = { boutique: '#E2704A', moyen: '#38BDF8', gms: '#A78BFA', corner: '#34D399' };
export const POS_BADGE = { entree: '€', coeur: '◆', premium: '★' };

const E = geo.vues.europe;
const DUREE_ZOOM = '1.15s';

const posPct = (vue, villeId) => {
  const p = geo.villes[villeId];
  if (!p) return null;
  return { left: ((p[0] - vue.x) / vue.w) * 100, top: ((p[1] - vue.y) / vue.h) * 100 };
};

const Fond = ({ vue }) => {
  const s = E.w / vue.w;
  const tx = (-(vue.x - E.x) / E.w) * 100;
  const ty = (-(vue.y - E.y) / E.h) * 100;
  return (
    <svg viewBox={`${E.x} ${E.y} ${E.w} ${E.h}`} preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full"
      style={{ transform: `scale(${s}) translate(${tx}%, ${ty}%)`, transformOrigin: '0 0', transition: `transform ${DUREE_ZOOM} cubic-bezier(0.4, 0, 0.2, 1)` }}>
      {geo.paths.map(p => (
        <path key={p.id} d={p.d}
          fill={p.id === 'fr' ? 'rgb(var(--accent-rgb) / 0.16)' : 'rgb(var(--ink-600) / 0.55)'}
          stroke={p.id === 'fr' ? 'rgb(var(--accent-rgb) / 0.65)' : 'rgb(var(--slate-500) / 0.45)'}
          strokeWidth={p.id === 'fr' ? 0.07 : 0.05} strokeLinejoin="round" />
      ))}
    </svg>
  );
};

const LogoBadge = ({ ville, pos, mags, company, selectionne, totalDays, onClick, index }) => {
  const types = [...new Set(mags.map(x => x.magasin.typeId).filter(Boolean))];
  const domination = types.length >= NB_TYPES_MAGASIN;
  const ev = mags.map(x => evenementLocalActif(x.magasin, totalDays)).find(Boolean);
  return (
    <button type="button" onClick={onClick}
      className="absolute z-20 badge-plant group"
      style={{ left: `${pos.left}%`, top: `${pos.top}%`, transition: `left ${DUREE_ZOOM} cubic-bezier(0.4,0,0.2,1), top ${DUREE_ZOOM} cubic-bezier(0.4,0,0.2,1)`, animationDelay: `${index * 0.1}s` }}
      data-testid={`carte-drapeau-${ville.id}`}
      title={`${ville.nom} — ${mags.length} magasin${mags.length > 1 ? 's' : ''}`}>
      <span className="relative block">
        <span className={`block rounded-full p-[3px] shadow-lg transition-transform group-hover:scale-110 ${domination
          ? 'ring-2 ring-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.65)] bg-amber-400/20'
          : selectionne ? 'ring-2 ring-[rgb(var(--accent-rgb))] bg-ink-900/80' : 'ring-2 ring-white/70 bg-ink-900/80'}`}>
          <span className="block rounded-full overflow-hidden">
            <CompanyLogo company={company} size={26} />
          </span>
        </span>
        {mags.length > 1 && (
          <span className="absolute -top-1.5 -right-2 text-[8px] font-black bg-ink-950 text-white border border-white/50 rounded-full px-1 leading-3 py-[1px]" data-testid={`carte-compteur-${ville.id}`}>
            ×{mags.length}
          </span>
        )}
        {domination && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-amber-400 text-[11px] drop-shadow" data-testid={`carte-domination-${ville.id}`}>★</span>}
        {ev && <span className="absolute -top-2 -left-2.5 text-sm animate-bounce drop-shadow" title={ev.nom} data-testid={`carte-evenement-${ville.id}`}>{ev.emoji}</span>}
        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-[2px]">
          {types.map(t => (
            <span key={t} className="inline-block w-[7px] h-[7px] rounded-full border border-black/40" style={{ background: FLAG_TYPE_COLORS[t] }} />
          ))}
        </span>
      </span>
      <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 text-[9px] font-bold text-white bg-black/60 px-1.5 py-px rounded whitespace-nowrap">
        {ville.nom}
      </span>
    </button>
  );
};

const VilleLibre = ({ ville, pos, selectionne, onClick }) => (
  <button type="button" onClick={onClick}
    className="absolute z-10 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center group"
    style={{ left: `${pos.left}%`, top: `${pos.top}%`, transition: `left ${DUREE_ZOOM} cubic-bezier(0.4,0,0.2,1), top ${DUREE_ZOOM} cubic-bezier(0.4,0,0.2,1)` }}
    data-testid={`carte-ville-${ville.id}`}>
    <span className="relative flex h-2.5 w-2.5">
      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-50 ${selectionne ? 'bg-amber-400' : 'bg-slate-300'}`} />
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 border ${selectionne ? 'bg-amber-400 border-amber-200' : 'bg-slate-200 border-slate-500 group-hover:bg-white'}`} />
    </span>
    <span className={`mt-0.5 text-[9px] font-bold px-1 py-px rounded whitespace-nowrap ${selectionne ? 'text-amber-200 bg-black/70' : 'text-white/90 bg-black/45 group-hover:bg-black/70'}`}>
      {ville.nom}
    </span>
  </button>
);

/**
 * Carte unique du réseau : contours réels (GeoJSON projeté), villes en lat/lon
 * exacts. La vue France se dézoome en vue Europe avec une transition animée.
 */
export const CarteReseau = ({ vue = 'france', company, parVille, villeChoisie, onSelect, totalDays = 0, retail = null }) => {
  const v = geo.vues[vue] || geo.vues.france;
  let idx = 0;
  return (
    <div className="relative w-full overflow-hidden rounded-xl border border-ink-600 select-none bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950"
      style={{ aspectRatio: `${geo.aspect}` }} data-testid="carte-reseau" data-vue={vue}>
      <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(var(--slate-600) / 0.25) 1px, transparent 0)', backgroundSize: '18px 18px' }} />
      <Fond vue={v} />
      {VILLES.filter(ville => vue === 'europe' || !ville.international).map(ville => {
        const pos = posPct(v, ville.id);
        if (!pos || pos.left < -3 || pos.left > 103 || pos.top < -3 || pos.top > 103) return null;
        const mags = parVille[ville.id] || [];
        const conc = retail ? concurrentDansVille(retail, ville.id) : null;
        const franchise = (retail?.franchise?.franchises || []).find(f => f.villeId === ville.id) || null;
        const badgeFranchise = franchise && (
          <span key={`f-${ville.id}`} className="absolute z-30 pointer-events-none"
            style={{ left: `calc(${pos.left}% - 26px)`, top: `calc(${pos.top}% - 16px)`, transition: `left ${DUREE_ZOOM} cubic-bezier(0.4,0,0.2,1), top ${DUREE_ZOOM} cubic-bezier(0.4,0,0.2,1)` }}
            title={`Franchisé : ${franchise.nom} — signé J${franchise.signeJour}`} data-testid={`carte-franchise-${ville.id}`}>
            <span className="flex items-center justify-center w-[18px] h-[18px] rounded-full bg-amber-400 text-ink-900 text-[10px] font-black shadow border-2 border-dashed border-amber-700">
              F
            </span>
          </span>
        );
        const concLogo = conc ? logoEnseigne(conc.enseigneId, company?.customLogo ? null : company?.logo) : null;
        const badgeConc = conc && (
          <span key={`c-${ville.id}`} className="absolute z-30 pointer-events-none"
            style={{ left: `calc(${pos.left}% + 12px)`, top: `calc(${pos.top}% - 16px)`, transition: `left ${DUREE_ZOOM} cubic-bezier(0.4,0,0.2,1), top ${DUREE_ZOOM} cubic-bezier(0.4,0,0.2,1)` }}
            title={`${getEnseigne(conc.enseigneId).nom} — implanté depuis J${conc.jour}`} data-testid={`carte-concurrent-${ville.id}`}>
            {concLogo ? (
              <span className="flex items-center justify-center w-[18px] h-[18px] rounded bg-white shadow overflow-hidden" style={{ border: `1.5px solid ${getEnseigne(conc.enseigneId).couleur}` }}>
                <img src={concLogo.src} alt={getEnseigne(conc.enseigneId).nom} className="w-full h-full object-contain" />
              </span>
            ) : (
              <span className="flex items-center justify-center w-4 h-4 rounded text-[9px] font-black text-white border border-black/40 shadow" style={{ background: getEnseigne(conc.enseigneId).couleur }}>
                {getEnseigne(conc.enseigneId).initiale}
              </span>
            )}
          </span>
        );
        if (mags.length > 0) {
          idx += 1;
          return (
            <React.Fragment key={ville.id}>
              <LogoBadge ville={ville} pos={pos} mags={mags} company={company}
                selectionne={villeChoisie === ville.id} totalDays={totalDays}
                onClick={() => onSelect(ville.id)} index={idx - 1} />
              {badgeConc}
              {badgeFranchise}
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={ville.id}>
            <VilleLibre ville={ville} pos={pos}
              selectionne={villeChoisie === ville.id} onClick={() => onSelect(ville.id)} />
            {badgeConc}
            {badgeFranchise}
          </React.Fragment>
        );
      })}
    </div>
  );
};
